import React from "react";
import { ISeat, IRacket, IProduct, ITotalCartItems } from '@/pages/Cart/types';
import { sortCourts } from '@/utils/payment/cancelExpiredItems';
import { isRacketForCourt, isRacketAttachedToCourt } from './racketHelpers';
import { CartItem } from '@/pages/Cart/components/CartItem';
import { ProductItem } from '@/pages/Cart/components/ProductItem';
import { StorageCartItem } from '@/pages/Cart/components/StorageCartItem';

interface RenderCartItemsProps {
  cartItems: ITotalCartItems;
  onDeleteProduct: (productId: number) => void;
  onIncreaseProduct: (productId: number) => void;
  onDecreaseProduct: (productId: number) => void;
}

export function renderCartItems({
  cartItems,
  onDeleteProduct,
  onIncreaseProduct,
  onDecreaseProduct,
}: RenderCartItemsProps): React.ReactNode[] {
  const elements: React.ReactNode[] = [];

  const seats = cartItems.seats || {};
  if (Object.keys(seats).length > 0) {
    const sortedSeats = sortCourts(seats as any);

    sortedSeats.forEach((seat) => {
      elements.push(
        <CartItem
          key={`seat-${seat.id}`}
          itemId={seat.id}
          rentType="seats"
          rentDate={seat.date}
          time={seat.time}
          price={seat.price || 0}
          courtTitle={seat.courtTitle}
          arenaTitle={seat.arenaTitle}
          isAttachedToCourt={false}
        />
      );

      const rackets = cartItems.rackets || {};
      Object.values(rackets).forEach((racket: any) => {
        const racketItem = racket as IRacket;
        if (isRacketForCourt(racketItem, seat)) {
          const racketPrice = (racketItem.price || 0) * (racketItem.count || 1);
          elements.push(
            <CartItem
              key={`racket-${racketItem.id}`}
              itemId={racketItem.id}
              rentType="rackets"
              rentDate={racketItem.date}
              time={racketItem.time}
              price={racketPrice}
              courtTitle={racketItem.courtTitle}
              arenaTitle={undefined}
              isAttachedToCourt={true}
            />
          );
        }
      });
    });
  }

  const rackets = cartItems.rackets || {};
  Object.values(rackets).forEach((racket: any) => {
    const racketItem = racket as IRacket;
    if (!isRacketAttachedToCourt(racketItem)) {
      const racketPrice = (racketItem.price || 0) * (racketItem.count || 1);
      elements.push(
        <CartItem
          key={`racket-${racketItem.id}`}
          itemId={racketItem.id}
          rentType="rackets"
          rentDate={racketItem.date}
          time={racketItem.time}
          price={racketPrice}
          courtTitle={undefined}
          arenaTitle={undefined}
          isAttachedToCourt={false}
        />
      );
    }
  });

  const products = cartItems.products || {};
  if (Object.keys(products).length > 0) {
    Object.values(products).forEach((product: any) => {
      const productItem = product as IProduct;
      elements.push(
        <ProductItem
          key={`product-${productItem.productId}`}
          product={productItem}
          onDelete={onDeleteProduct}
          onIncrease={onIncreaseProduct}
          onDecrease={onDecreaseProduct}
        />
      );
    });
  }

  const storage = cartItems.storage || {};
  if (Object.keys(storage).length > 0) {
    Object.entries(storage).forEach(([key, item]: [string, any]) => {
      elements.push(
        <StorageCartItem
          key={`storage-${key}`}
          storageKey={key}
          item={item}
          onDelete={onDeleteProduct}
        />
      );
    });
  }

  return elements;
}
